<template>
    <div>
        <!-- mobile-breakpoint is set to 0 to avoid losing the table headers on mobile -->
        <v-data-table
            ref="dataTable"
            item-key="id"
            :headers="headers"
            :items="items ?? []"
            :items-per-page="size"
            :loading="isFetching"
            single-expand
            :show-expand="shouldRowsExpand"
            :expanded.sync="expanded"
            mobile-breakpoint="0"
            hide-default-footer
        >
            <template v-slot:top>
                <div v-if="hasSearchField" class="search-field">
                    <v-text-field v-model="filter" dense outlined hide-details label="Filter" class="filter-input" />
                    <button-search :disabled="isFetching" :callback="updateFilterCallback" />
                </div>
            </template>
            <template v-slot:item="{ item, expand, isExpanded }">
                <tr>
                    <td
                        v-for="header in headers"
                        :key="header.value"
                        :class="{ 'centered-cell': header.align === 'center' }"
                    >
                        <span v-if="header.text === 'Id'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === 'User'"> {{ item.user.username }}</span>
                        <span v-else-if="header.text === 'Mobile Number'"> {{ item.recipient }}</span>
                        <span v-else-if="header.text === 'Text'"> {{ item.viberText ? item.viberText : item.smsText ? item.smsText : 'N/A' }}</span>
                        <span v-else-if="header.text === 'SMS Parts'"> {{ item.parts }}</span>
                        <span v-else-if="header.text === 'Chars'"> {{ item.chars }}</span>
                        <span v-else-if="header.text === 'Is UTF-8'"> {{ item.utf8 }}</span>
                        <span v-else-if="header.text === 'Date Sent'"> {{ item.sentDate ? item.sentDate : 'N/A' }}</span>
                        <span v-else-if="header.text === 'Status Date'"> {{ item.statusDate ? item.statusDate : 'N/A' }}</span>
                        <span v-else-if="header.text === 'Username' && $route.name === 'Users'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === 'Admin'"> {{ item.parent? item.parent.username : '-' }}</span>
                        <span v-else-if="header.text === 'Delivery Rate'"> {{ item.deliveryRate ? item.deliveryRate : '0' }} </span>
                        <div v-else-if="header.text === 'Enabled' && $route.name === 'Users'">
                            <two-state-icon :condition="item.active" />
                        </div>
                        <span v-else-if="header.text === 'Username' && $route.name === 'Active Hours'"> {{ item.user.username }}</span>
                        <div v-else-if="header.text === 'Enabled' && $route.name === 'Active Hours'">
                            <v-switch
                                v-model="item.enabled"
                                class="form-field"
                                color="#4caf50"
                                @click="enableHour(item)"
                            />
                        </div>
                        <span v-else-if="header.text === 'Start Time'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === 'End Time'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === '#'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === 'Originator'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === 'Alias'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === 'TTL'"> {{ item[header.value] }}</span>
                        <span v-else-if="header.text === 'Scheduled At'"> {{ item[header.value]? getDateAndTimeInFormat(item[header.value]) : 'N/A' }}</span>
                        <span v-else-if="header.text === 'Started At'"> {{ item[header.value]? getDateAndTimeInFormat(item[header.value]) : 'N/A' }}</span>
                        <span v-else-if="header.text === 'Completed At'"> {{ item[header.value]? getDateAndTimeInFormat(item[header.value]) : 'N/A' }}</span>
                        <span v-else-if="header.text === 'Created At'"> {{ item[header.value]? getDateAndTimeInFormat(item[header.value]) : 'N/A' }}</span>
                        <div v-else-if="header.text === 'Status' && isCreateOrInfoRoute && showIcons">
                            <div
                                :style="{ color: MSG_STATUS_COLORS[item.status] }"
                            > {{ item.status }}</div>
                        </div>
                        <div v-else-if="header.text === 'Status' && !isCreateOrInfoRoute && showIcons">
                            <v-icon
                                v-if="item.completedAt || item.status==='DELIVERED'"
                                color="success"
                                class="inline-icon"
                            >
                                mdi-check-circle-outline
                            </v-icon>
                            <v-icon
                                v-else-if="item.status==='EXPIRED'"
                                color="red"
                                class="inline-icon"
                            >
                                mdi-clock-alert
                            </v-icon>
                            <v-icon
                                v-else
                                color="orange"
                                class="inline-icon"
                            >
                                mdi-clock-outline
                            </v-icon>
                        </div>
                        <div
                            v-else-if="header.text === 'Status' && !showIcons"
                            :style="{ color: MSG_STATUS_COLORS[item.status] }"
                        >
                            {{ item.status }}
                        </div>
                        <div v-else-if="header.text === 'Fallback'">
                            <v-icon
                                v-if="item.fallback"
                                color="success"
                                class="inline-icon"
                            >
                                mdi-check-circle-outline
                            </v-icon>
                            <v-icon
                                v-else
                                color="error"
                                class="inline-icon"
                            >
                                mdi-close-circle-outline
                            </v-icon>
                        </div>
                        <div v-else-if="header.text === 'Actions'" class="text-center">
                            <div v-if="isUsers" class="action-btns" @click="viewDetails(item)">
                                <v-btn
                                    depressed
                                    color="success"
                                >
                                    <v-icon
                                        class="inline-icon"
                                    >
                                        mdi-eye
                                    </v-icon>
                                    Show Details
                                </v-btn>
                            </div>
                            <div v-if="isActiveHours" class="action-btns">
                                <delete-dialog
                                    @delete-event="deleteHour(item.id)"
                                />
                            </div>
                            <div v-if="isViberCampaign" class="action-btns">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="showViberCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-eye
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>View</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            :disabled="item.fileUploaded || isEditDisabled"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="editViberCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="warning"
                                            v-on="on"
                                            @click="duplicateViberCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-content-copy
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Duplicate</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            :disabled="isEditDisabled"
                                            :class="[ !item.paused ? 'status-color-warning' : 'status-color-success']"
                                            depressed
                                            v-on="on"
                                            @click="updateViberCampaignStatus(item.id, !item.paused)"
                                        >
                                            <v-icon
                                                v-if="!item.paused"
                                                class="inline-icon"
                                            >
                                                mdi-pause-circle-outline
                                            </v-icon>
                                            <v-icon
                                                v-else
                                                class="inline-icon"
                                            >
                                                mdi-play
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ item.paused ? 'Unpause' : 'Pause' }}</span>
                                </v-tooltip>
                                <delete-dialog
                                    :disable-delete="!isDeleteDisabled"
                                    show-tooltip
                                    @delete-event="deleteViberCampaign(item.id)"
                                />
                                <v-icon
                                    @click="expand(!isExpanded)"
                                >
                                    {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                </v-icon>
                            </div>
                            <div v-if="isSMSCampaign" class="action-btns">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="showSMSCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-eye
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>View</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            :disabled="item.fileUploaded || isEditDisabled"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="editSMSCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="warning"
                                            v-on="on"
                                            @click="duplicateSMSCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-content-copy
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Duplicate</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            :disabled="isEditDisabled"
                                            :class="[ !item.paused ? 'status-color-warning' : 'status-color-success']"
                                            depressed
                                            v-on="on"
                                            @click="updateSMSCampaignStatus(item.id, !item.paused)"
                                        >
                                            <v-icon
                                                v-if="!item.paused"
                                                class="inline-icon"
                                            >
                                                mdi-pause-circle-outline
                                            </v-icon>
                                            <v-icon
                                                v-else
                                                class="inline-icon"
                                            >
                                                mdi-play
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ item.paused ? 'Unpause' : 'Pause' }}</span>
                                </v-tooltip>
                                <delete-dialog
                                    :disable-delete="!isDeleteDisabled"
                                    show-tooltip
                                    @delete-event="deleteSmsCampaign(item.id)"
                                />
                                <v-icon
                                    @click="expand(!isExpanded)"
                                >
                                    {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                </v-icon>
                            </div>
                            <div v-if="isTestList" class="action-btns">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            :disabled="isEditDisabled"
                                            v-bind="attrs"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="editTestListDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit</span>
                                </v-tooltip>
                                <delete-dialog
                                    :disable-delete="!isDeleteDisabled"
                                    show-tooltip
                                    @delete-event="deleteTestList(item.id)"
                                />
                            </div>
                            <div v-if="isViberRecurringCampaign" class="action-btns">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="showRecurringViberCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-eye
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>View</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-if="!isEditDisabled"
                                            v-bind="attrs"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="editRecurringViberCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="warning"
                                            v-on="on"
                                            @click="duplicateViberReccuringCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-content-copy
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Duplicate</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            :disabled="isEditDisabled"
                                            :class="[ !item.paused ? 'status-color-warning' : 'status-color-success']"
                                            depressed
                                            v-on="on"
                                            @click="updateRecurringViberCampaignStatus(item.id, !item.paused)"
                                        >
                                            <v-icon
                                                v-if="!item.paused"
                                                class="inline-icon"
                                            >
                                                mdi-pause-circle-outline
                                            </v-icon>
                                            <v-icon
                                                v-else
                                                class="inline-icon"
                                            >
                                                mdi-play
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ item.paused ? 'Unpause' : 'Pause' }}</span>
                                </v-tooltip>
                                <delete-dialog
                                    :disable-delete="!isDeleteDisabled"
                                    show-tooltip
                                    @delete-event="deleteRecurringViberCampaign(item.id)"
                                />
                            </div>
                            <div v-if="isSMSRecurringCampaign" class="action-btns">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="showRecurringSMSCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-eye
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>View</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-if="!isEditDisabled"
                                            v-bind="attrs"
                                            depressed
                                            color="success"
                                            v-on="on"
                                            @click="editRecurringSMSCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-pencil
                                            </v-icon></v-btn>
                                    </template>
                                    <span>Edit</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            depressed
                                            color="warning"
                                            v-on="on"
                                            @click="duplicateSMSRecurringCampaignDetails(item.id)"
                                        >
                                            <v-icon
                                                class="inline-icon"
                                            >
                                                mdi-content-copy
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Duplicate</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            :disabled="isEditDisabled"
                                            :class="[ !item.paused ? 'status-color-warning' : 'status-color-success']"
                                            depressed
                                            v-on="on"
                                            @click="updateRecurringSMSCampaignStatus(item.id, !item.paused)"
                                        >
                                            <v-icon
                                                v-if="!item.paused"
                                                class="inline-icon"
                                            >
                                                mdi-pause-circle-outline
                                            </v-icon>
                                            <v-icon
                                                v-else
                                                class="inline-icon"
                                            >
                                                mdi-play
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ item.paused ? 'Unpause' : 'Pause' }}</span>
                                </v-tooltip>
                                <delete-dialog
                                    :disable-delete="!isDeleteDisabled"
                                    show-tooltip
                                    @delete-event="deleteRecurringSMSCampaign(item.id)"
                                />
                            </div>
                            <div v-if="isTransactional">
                                <v-btn
                                    depressed
                                    color="success"
                                    @click="setActiveItem(item)"
                                >
                                    <v-icon> mdi-eye </v-icon>
                                </v-btn>
                            </div>
                        </div>
                        <v-icon
                            v-else-if="header.value === 'data-table-expand'"
                            :disabled="(item.details ?? []).length === 0"
                            @click="expand(!isExpanded)"
                        >
                            {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                        </v-icon>
                        <span v-else>{{ item[header.value] }}</span>
                    </td>
                </tr>
            </template>
            <template v-slot:expanded-item="props">
                <td v-if="(isViberCampaign || isSMSCampaign) && hasTestListPrivilege" class="item-details" :colspan="props.headers.length">
                    <div class="item-details-header test-header">
                        Please Select testing List to send test campaign
                    </div>
                    <div class="test-container">
                        <v-select
                            v-model="selectedTestList"
                            clearable
                            label="Select Test List"
                            :items="testLists"
                            item-text="name"
                            item-value="id"
                        />
                        <v-btn
                            v-if="isViberCampaign"
                            :disabled="disableViberCampaignForTest.includes(props.item.id)"
                            color="success"
                            @click="sendTestViberCampaign(props.item.id, selectedTestList)"
                        >
                            <v-icon>
                                mdi-arrow-right
                            </v-icon>
                        </v-btn>
                        <v-btn
                            v-else-if="isSMSCampaign"
                            :disabled="disableSMSCampaignForTest.includes(props.item.id)"
                            color="success"
                            @click="sendTestSMSCampaign(props.item.id, selectedTestList)"
                        >
                            <v-icon>
                                mdi-arrow-right
                            </v-icon>
                        </v-btn>
                    </div>
                    <alert :show-alert="showAlert" :message="alertMessage" :type="alertType" />

                </td>
                <td v-else class="item-details" :colspan="props.headers.length">
                    <div v-for="detail in props.item.details" :key="detail.header" class="item-details-content">
                        <span class="item-details-header">{{ detail.header }} :</span>
                        <span>{{ detail.value }}</span>
                    </div>
                </td>
            </template>
        </v-data-table>
        <v-row v-if="totalPages" class="pagination">
            <v-col cols="10">
                <v-pagination
                    v-model="localPage"
                    :length="totalPages"
                    total-visible="7"
                    color="black"
                    circle
                    @input="paginationChange"
                />
            </v-col>
            <v-col cols="2" class="page-size-selector">
                <v-select
                    v-model="localSize"
                    :hint="`Items per page: ${localSize}`"
                    :items="sizes"
                    label="Select"
                    persistent-hint
                    return-object
                    single-line
                    @input="sizeChange"
                />
                Total items received: {{ totalItems }}
            </v-col>
        </v-row>
    </div>
</template>

<script>
    import activeHoursService from '@/services/active-hours-service'
    import dateTimeUtils from '@/utils/date-time-utils'
    import viberService from '@/services/viber-campaign-service'
    import { MSG_STATUS_COLORS, PRIVILEGES } from '@/utils/consts'
    import smsService from '@/services/sms-campaign-service'
    import testListService from '@/services/test-list-service'
    import recurringViberService from '@/services/recurring-viber-campaign-service'
    import recurringSmsService from '@/services/recurring-sms-campaign-service'
    import DeleteDialog from '@/components/DeleteDialog.vue'
    import TwoStateIcon from '@/components/TwoStateIcon.vue'
    import ButtonSearch from '@/components/ButtonSearch.vue'
    import Alert from '@/components/Alert.vue'
    import { hasPrivilege } from '@/utils/userUtils'
    export default {
        components: { ButtonSearch, DeleteDialog, TwoStateIcon, Alert },
        props: {
            shouldRowsExpand: {
                type: Boolean,
                required: false,
                default: false
            },
            items: {
                type: Array,
                default: null
            },
            headers: {
                type: Array,
                default: null
            },
            totalPages: {
                type: Number,
                default: null
            },
            page: {
                type: Number,
                default: null
            },
            size: {
                type: Number,
                default: null
            },
            type: {
                type: String,
                default: null
            },
            totalItems: {
                type: Number,
                required: false,
                default: null
            },
            showIcons: {
                type: Boolean,
                required: false,
                default: true
            },
            isEditDisabled: {
                type: Boolean,
                required: false,
                default: false
            },
            isDeleteDisabled: {
                type: Boolean,
                required: false,
                default: false
            },
            pageSizeOptions: {
                type: Array,
                required: false,
                default: () => [5, 10, 30, 60, 100]
            },
            hasSearchField: {
                type: Boolean,
                required: false,
                default: false
            },
            updateFilter: {
                type: Function,
                required: false,
                default: () => {}
            },
            isFetching: {
                type: Boolean,
                required: true
            }
        },
        data() {
            return {
                localPage: 1,
                testLists: [],
                selectedTestList: '',
                localSize: this.size,
                sizes: this.pageSizeOptions,
                actions: [
                    {
                        text: 'Edit',
                        value: 'Edit'
                    }
                ],
                activateHour: false,
                filter: '',
                expanded: [],
                showAlert: false,
                alertMessage: '',
                alertType: 'error',
                isBtnDisabledAfterSent: false,
                disableViberCampaignForTest: [],
                disableSMSCampaignForTest: []
            }
        },
        computed: {
            isSMSCampaign() {
                return this.$route.name === 'SMS Campaigns'
            },
            isViberCampaign() {
                return this.$route.name === 'Viber Campaigns'
            },
            isViberRecurringCampaign() {
                return this.$route.name === 'Recurring Viber Campaigns'
            },
            isSMSRecurringCampaign() {
                return this.$route.name === 'Recurring SMS Campaigns'
            },
            isTransactional() {
                return this.$route.name.includes('Transactional')
            },
            isActiveHours() {
                return this.$route.name === 'Active Hours'
            },
            isTestList() {
                return this.$route.name === 'Test List'
            },
            isUsers() {
                return this.$route.name === 'Users'
            },
            isCreateOrInfoRoute() {
                return ['Create', 'Details'].some(word => this.$route.name.includes(word))
            },
            hasTestListPrivilege() {
                return hasPrivilege(
                    PRIVILEGES.TESTLIST_READ_PRIVILEGE,
                    this.$store.getters.getCurrentUserPrivileges
                )
            },
            MSG_STATUS_COLORS() {
                return MSG_STATUS_COLORS
            }
        },
        created() {
            if (this.hasTestListPrivilege) {
                this.fetchAllTestLists()
            }
            this.localPage = this.page
            this.localSize = this.size
        },
        methods: {
            viewDetails(item) {
                this.$router.push(`/users/${item.id}`)
            },
            setAlert(message, type) {
                this.showAlert = true
                this.alertMessage = message
                this.alertType = type
            },
            fetchAllTestLists() {
                testListService.getAllTestLists().then((resp) => {
                    this.testLists = resp.data.content
                })
            },
            sendTestViberCampaign(campaingId, testListid) {
                this.disableViberCampaignForTest.push(campaingId)
                viberService.sentTestViberCampaign(campaingId, testListid).then((resp) => {
                    this.setAlert('Test List Sent was a success', 'success')
                }).catch(() => {
                    this.disableViberCampaignForTest.pop()
                    this.setAlert('Something went wrong please try again', 'error')
                })
                this.selectedTestList = ''
                setTimeout(() => { this.showAlert = false }, 3000)
            },
            sendTestSMSCampaign(campaingId, testListid) {
                this.disableSMSCampaignForTest.push(campaingId)
                smsService.sentTestSMSCampaign(campaingId, testListid).then((resp) => {
                    this.setAlert('Test List Sent was a success', 'success')
                }).catch(() => {
                    this.disableSMSCampaignForTest.pop()
                    this.setAlert('Something went wrong please try again', 'error')
                })
                this.selectedTestList = ''
                setTimeout(() => { this.showAlert = false }, 3000)
            },
            duplicateViberCampaignDetails(id) {
                viberService.duplicateViberCampaign(id).then(() => {
                    this.$emit('refresh-data')
                })
            },
            duplicateViberReccuringCampaignDetails(id) {
                recurringViberService.duplicateViberRecurringCampaign(id).then(() => {
                    this.$emit('refresh-data')
                })
            },
            duplicateSMSCampaignDetails(id) {
                smsService.duplicateSMSCampaign(id).then(() => {
                    this.$emit('refresh-data')
                })
            },
            duplicateSMSRecurringCampaignDetails(id) {
                recurringSmsService.duplicateSMSRecurringCampaign(id).then(() => {
                    this.$emit('refresh-data')
                })
            },
            getDateAndTimeInFormat(date) {
                return dateTimeUtils.getDateTimeInFormatAndTimezone(date, 'Europe/Athens', 'YYYY-MM-DD HH:mm:ss')
            },
            paginationChange() {
                this.$emit('paginationChange', this.localPage, this.localSize)
            },
            showViberCampaignDetails(id) {
                this.$router.push(`/viber/${id}`)
            },
            showSMSCampaignDetails(id) {
                this.$router.push(`/sms/${id}`)
            },
            showRecurringViberCampaignDetails(id) {
                this.$router.push(`/recurring-viber/${id}`)
            },
            showRecurringSMSCampaignDetails(id) {
                this.$router.push(`/recurring-sms/${id}`)
            },
            editRecurringSMSCampaignDetails(id) {
                this.$router.push(`/recurring-sms/${id}/edit`)
            },
            editRecurringViberCampaignDetails(id) {
                this.$router.push(`/recurring-viber/${id}/edit`)
            },
            editTestListDetails(id) {
                this.$router.push(`/test-list/${id}/edit`)
            },
            editViberCampaignDetails(id) {
                this.$router.push(`/viber/${id}/edit`)
            },
            editSMSCampaignDetails(id) {
                this.$router.push(`/sms/${id}/edit`)
            },
            sizeChange() {
                this.localPage = 1
                this.paginationChange()
            },
            deleteHour(id) {
                activeHoursService.deleteActiveHour(id).then(() => {
                    this.$emit('deleteActiveHour', id)
                })
            },
            enableHour(item) {
                activeHoursService.updateActiveHour({ enabled: item.enabled }, item.id)
            },
            updateViberCampaignStatus(id, status) {
                viberService.updateViberCampaignStatus(id, status)
                    .then(() => {
                        this.$emit('updateCampaign', id, 'paused', status)
                    })
            },
            updateSMSCampaignStatus(id, status) {
                smsService.updateSMSCampaignStatus(id, status)
                    .then(() => {
                        this.$emit('updateCampaign', id, 'paused', status)
                    })
            },
            updateRecurringViberCampaignStatus(id, status) {
                recurringViberService.updateViberCampaignStatus(id, status).then(() => {
                    this.$emit('updateCampaign', id, 'paused', status)
                })
            },
            updateRecurringSMSCampaignStatus(id, status) {
                recurringSmsService.updateSMSCampaignStatus(id, status).then(() => {
                    this.$emit('updateCampaign', id, 'paused', status)
                })
            },
            deleteRecurringViberCampaign(id) {
                recurringViberService.deleteViberCampaign(id).then(() => {
                    this.$emit('deleteCampaign', id)
                })
            },
            deleteSmsCampaign(id) {
                smsService.deleteSMSCampaign(id).then(() => {
                    this.$emit('deleteCampaign', id)
                })
            },
            deleteTestList(id) {
                testListService.deleteTestList(id).then(() => {
                    this.$emit('deleteTestList', id)
                })
            },
            deleteViberCampaign(id) {
                viberService.deleteViberCampaign(id).then(() => {
                    this.$emit('deleteCampaign', id)
                })
            },
            deleteRecurringSMSCampaign(id) {
                recurringSmsService.deleteSMSCampaign(id).then(() => {
                    this.$emit('deleteCampaign', id)
                })
            },
            setActiveItem(item) {
                this.$emit('set-active-item', item)
            },
            updateFilterCallback() {
                this.updateFilter(this.filter)
            }
        }
    }
</script>

<style>
@media (max-width: 992px) {
    .v-data-table-header .text-start {
        text-wrap: nowrap;
    }
}
</style>

<style scoped>
    .pagination {
        padding: 1rem;
        justify-content: center;
    }

    .no-data-text {
        color: red;
        text-align: center;
        font-size: 1.2rem;
        margin-top: 25px;
    }

    .active {
        text-align: center;
        border-radius: 5px;
        width: 50px;
        padding: 5px;
        color: white;
        background: green;
        font-weight: 500;
    }

    .action-btns {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px !important;
    }

    .test-container {
        width: 500px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        padding: 1rem;
    }

    .test-header {
        font-weight: bold;
    }

    .disable {
        text-align: center;
        width: 50px;
        padding: 5px;
        border-radius: 5px;
        color: white;
        background: red;
        font-weight: 500;
    }

    .inline-icon {
        vertical-align: bottom;
        font-size: 22px !important;
    }

    .status-color-success {
        background-color: #4caf50 !important;
        color: white;
    }

    .status-color-warning {
        background-color: #fb8c00 !important;
        color: white;
    }

    .status-color-error {
        background-color: red !important;
        color: white;
    }

    .centered-cell {
        text-align: center;
    }

    .page-size-selector {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .search-field {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 10px;
        padding: 1rem;

        * {
            flex-grow: 0;
        }
    }

    .filter-input {
        flex: inherit;
    }

    .item-details {
        width: 100%;
        padding: 1rem;

        .item-details-content {
            display: flex;
            gap: 1rem;

            .item-details-header {
                font-weight: 900;
            }
        }
    }

    @media (max-width: 992px) {
        .page-size-selector {
            flex: auto;
            padding-top: 0;
            max-width: 150px;
        }

        td {
            text-wrap: nowrap;
        }
    }

</style>
